/**
 * Color Palette — 3-Layer System
 *
 * Layer 1 (this file): 6 core colors → derived clusters
 * Layer 2 (intent.ts): Semantic intents referencing cluster stops
 * Layer 3 (buttons.ts, labels.ts): Component presets referencing intents or clusters
 */
import { colord } from 'colord';

const alpha   = (color: string, a: number) => colord(color).alpha(a).toRgbString();
// MUI-compatible: multiply each RGB channel by (1 - coeff)
const darken  = (color: string, coeff: number) => {
  const { r, g, b, a } = colord(color).toRgb();
  return colord({ r: Math.round(r * (1 - coeff)), g: Math.round(g * (1 - coeff)), b: Math.round(b * (1 - coeff)), a }).toHex();
};
// MUI-compatible: add (100 - L) * coeff to HSL lightness
const lighten = (color: string, coeff: number) => {
  const hsl = colord(color).toHsl();
  hsl.l = Math.min(100, hsl.l + (100 - hsl.l) * coeff);
  return colord(hsl).toHex();
};

// ─── Core Colors ───────────────────────────────────────────────────────────────

const SURFACE = '#FDFFFC';
const NEUTRAL = '#434F4D';
const PRIMARY = '#428BCA';
const SUCCESS = '#5CB85C';
const DANGER  = '#C42021';
const WARNING = '#F18F01';

// ─── Cluster Helpers ───────────────────────────────────────────────────────────

function createChromatic(base: string) {
  return {
    tint:  alpha(base, 0.08),
    light: alpha(base, 0.15),
    base,
    hover: darken(base, 0.15),
    dark:  darken(base, 0.30),
  } as const;
}

// ─── Clusters ──────────────────────────────────────────────────────────────────

export const surface = {
  lighter: lighten(SURFACE, 0.3),
  base:    SURFACE,
  muted:   darken(SURFACE, 0.04),
  border:  darken(SURFACE, 0.10),
} as const;

export const neutral = {
  subtle:  lighten(NEUTRAL, 0.25),
  base:    NEUTRAL,
  dark:    darken(NEUTRAL, 0.25),
  darker:  darken(NEUTRAL, 0.45),
} as const;

export const primary = createChromatic(PRIMARY);
export const success = createChromatic(SUCCESS);
export const danger  = createChromatic(DANGER);
export const warning = createChromatic(WARNING);

// ─── Chart Colors (data-viz only, not clustered) ──────────────────────────────

export const chart = {
  red:      '#F25F5C',
  orange:   '#EA963E',
  yellow:   '#F3CA40',
  green:    '#6AB33F',
  blue:     '#428BCA',
  gray:     '#808186',
  purple:   '#9c27b0',
  cyan:     '#00bcd4',
  brown:    '#795548',
  blueGrey: '#607D8B',
  empty:    '#e8e8e8',
  // "Not Viewed" status in the dashboard activity chart. The activity endpoint
  // sends counts without colors, so this fallback is what renders. It matches
  // the neutral gray the submissions endpoint sends for "Not Flagged" (#9E9E9E)
  // so the two "nothing happened" states read as the same gray side by side —
  // the near-white #F5F5F5 it replaced looked washed-out next to it.
  unviewed: '#9E9E9E',
} as const;

// ─── Fixed Colors ──────────────────────────────────────────────────────────────

export const fixed = {
  white: '#FFFFFF',
  black: '#000000',
} as const;

// ─── Header Chrome ─────────────────────────────────────────────────────────────

/** Controls on the dark header bar. `control` is the fill of the help icon
 * (public/assets/images/icons/help-icon.png, sampled rgb 210,200,198) so a
 * pill beside it (the signed-out Sign In, #1307) reads as the same family. */
export const chrome = {
  control: '#D2C8C6',
} as const;

// ─── MUI Palette ───────────────────────────────────────────────────────────────

export const palette = {
  primary: {
    main: primary.base,
    dark: primary.dark,
    light: primary.tint,
    contrastText: fixed.white,
  },
  secondary: {
    main: primary.base,
    dark: primary.hover,
    light: primary.tint,
    contrastText: fixed.white,
  },
  error: {
    main: danger.base,
    dark: danger.dark,
    light: danger.tint,
  },
  warning: {
    main: warning.base,
  },
  info: {
    main: primary.base,
  },
  success: {
    main: success.base,
  },
  grey: {
    50:  surface.base,
    100: surface.muted,
    200: surface.border,
    300: success.tint,
    400: neutral.subtle,
    500: neutral.subtle,
    600: neutral.subtle,
    700: neutral.base,
    800: neutral.dark,
    900: neutral.darker,
  },
  background: {
    default: surface.muted,
    paper: fixed.white,
  },
  text: {
    primary: neutral.dark,
    secondary: neutral.base,
  },
} as const;

// ─── Backward-Compatible Colors (deprecated) ──────────────────────────────────
// Preserves the old `colors.X` API so 117+ component files keep working.
// Migrate to cluster imports (surface, neutral, primary, etc.) then remove.

/** @deprecated Use cluster imports directly: surface, neutral, primary, success, danger, warning, chart, fixed */
export const colors = {
  // Greens → success cluster
  green:          success.base,
  greenDeep:      success.hover,
  greenLight:     success.light,
  greenDark:      success.dark,
  greenTint:      success.tint,
  greenTintHover: success.light,
  whiteGreen:     success.tint,

  // Blues → primary cluster
  blue:           primary.base,
  blueLight:      primary.tint,
  blueDark:       primary.hover,
  blueTint:       primary.tint,
  blueTintHover:  primary.light,

  // Reds → danger cluster
  red:            danger.base,
  redDark:        danger.dark,
  redLight:       danger.tint,

  // Oranges → warning cluster
  orange:         warning.base,
  orangeLight:    warning.tint,

  // Neutrals → neutral + surface clusters
  gray:           neutral.subtle,
  grayAccent:     neutral.subtle,
  grayLight:      darken(surface.border, 0.05),
  grayDark:       neutral.base,
  dark:           neutral.dark,
  light:          surface.muted,

  // Whites → surface cluster + fixed
  white:          fixed.white,
  whiteGray:      surface.muted,
  whiteGrayDark:  surface.border,
  black:          fixed.black,

  // Chart/sentiment — pass through
  uiRed:    chart.red,
  uiOrange: chart.orange,
  uiYellow: chart.yellow,
  uiGreen:  chart.green,
  uiBlue:   chart.blue,
  uiGray:   chart.gray,
  yellow:   chart.yellow,
  purple:   chart.purple,
  cyan:     chart.cyan,
  brown:    chart.brown,
  blueGrey: chart.blueGrey,
  chartEmpty: chart.empty,

  // Selected/active states — derived from primary
  selectedBlue:       darken(primary.base, 0.20),
  selectedBlueHover:  darken(primary.base, 0.30),
  selectedBlueActive: darken(primary.base, 0.10),

  // Button state colors — derived from clusters
  greenBorder:        darken(success.base, 0.05),
  greenDisabledBg:    alpha(success.base, 0.35),
  greenDisabledBorder: alpha(success.base, 0.30),
  greenDisabledText:  alpha(success.base, 0.20),

  blueBorder:         darken(primary.base, 0.08),
  blueHoverBorder:    darken(primary.base, 0.25),

  redBorder:          darken(danger.base, 0.05),
  redHoverBg:         darken(danger.base, 0.02),
  redHoverBorder:     darken(danger.base, 0.08),
  redDisabledBg:      alpha(danger.base, 0.30),
  redDisabledBorder:  alpha(danger.base, 0.25),

  disabledBorder:     surface.border,
  disabledText:       alpha(neutral.subtle, 0.50),
  darkHoverBorder:    darken(neutral.base, 0.10),
  disabledIcon:       alpha(neutral.subtle, 0.55),

  tabInactive:        alpha(neutral.subtle, 0.55),
  errorFaint:         alpha(danger.base, 0.12),
} as const;
